import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer } from 'redux-persist';

import { getReduxStorageUtil } from '../../../main/utils/storage/redux-storage.util.ts';

import { BALL_SLICE_NAME, BallAnimation, BallSliceState } from './ball.slice.types.ts';

export const BALL_SLICE_INITIAL_STATE: BallSliceState = {
  isVisible: true,
  isDraggable: false,
  xPosition: undefined,
  rowIndex: undefined,
  animation: null,
};

const persistKeys: (keyof BallSliceState)[] = [];

const ballSlice = createSlice({
  name: BALL_SLICE_NAME,
  initialState: BALL_SLICE_INITIAL_STATE,
  reducers: {
    resetBallSlice: (_state) => {
      return BALL_SLICE_INITIAL_STATE;
    },
    setBallXPosition: (state, action: PayloadAction<number>) => {
      state.xPosition = action.payload;
    },
    setBallIsVisible: (state, action: PayloadAction<boolean>) => {
      state.isVisible = action.payload;
    },
    resetBallAnimation: (state) => {
      state.animation = null;
    },
    resetBallPosition: (state) => {
      state.isVisible = false;
      state.isDraggable = false;
      state.xPosition = 0;
      state.rowIndex = undefined;
      state.animation = null;
    },
    setupBallAnimation: (state, action: PayloadAction<{ sequence: BallAnimation[] }>) => {
      state.animation = action.payload.sequence;
      state.isDraggable = false;
      state.isVisible = true;
    },
    completeBallAnimation: (state, action: PayloadAction<number>) => {
      state.xPosition = action.payload;
      state.animation = null;
    },
    toggleBallDraggability: (state, action: PayloadAction<boolean>) => {
      state.isDraggable = action.payload;
      state.animation = null;
    },
    positionBallAtWord: (
      state,
      action: PayloadAction<{ xPosition: number; rowIndex: number; isVisible?: boolean }>
    ) => {
      state.xPosition = action.payload.xPosition;
      state.rowIndex = action.payload.rowIndex;
      if (action.payload.isVisible !== undefined) {
        state.isVisible = action.payload.isVisible;
      }
    },
    moveBallToNextLine: (state) => {
      state.xPosition = 0;
      state.rowIndex = (state.rowIndex ?? -1) + 1;
    },
    moveBallToRow: (state, action: PayloadAction<number>) => {
      state.xPosition = 0;
      state.rowIndex = action.payload;
    },
    setInteractiveMode: (state, action: PayloadAction<boolean>) => {
      state.isDraggable = action.payload;
      state.isVisible = true;
    },
  },
});

const persistConfig: PersistConfig<BallSliceState> = {
  key: BALL_SLICE_NAME,
  storage: getReduxStorageUtil(),
  whitelist: persistKeys,
};

export const {
  // Individual property actions
  resetBallSlice,
  setBallXPosition,
  setBallIsVisible,
  resetBallAnimation,
  // Combined property actions
  resetBallPosition,
  setupBallAnimation,
  completeBallAnimation,
  toggleBallDraggability,
  positionBallAtWord,
  moveBallToNextLine,
  moveBallToRow,
  setInteractiveMode,
} = ballSlice.actions;

export const BallSlice = persistReducer(persistConfig, ballSlice.reducer);
